import type { ScanLocation } from '@/api/types';

/** The explicit state machine of the move-by-scan screen: Источник -> Назначение -> Товары. */
export type MoveStep = 'SOURCE' | 'DEST' | 'ITEMS';
export type MoveCell = { readonly id: string; readonly code: string; readonly kind: string };
export type MoveState = { readonly step: MoveStep; readonly source: MoveCell | null; readonly dest: MoveCell | null };

export const INITIAL_MOVE_STATE: MoveState = { step: 'SOURCE', source: null, dest: null };

export type LocationOutcome =
  | 'SOURCE_SET'
  | 'DEST_SET'
  /** A cell was scanned while products are being scanned: nothing changes until the operator asks to. */
  | 'RELOCATE_HINT'
  | 'SOURCE_NOT_ALLOWED'
  | 'DEST_NOT_ALLOWED'
  | 'QUARANTINE_OFF'
  | 'SAME_CELL';

const SOURCE_KINDS: readonly string[] = ['BIN', 'RECEIVING', 'QUARANTINE'];

const cellOf = (l: ScanLocation): MoveCell => ({ id: l.locationId, code: l.code, kind: l.kind });

/** Feeds a scanned (active) cell to the machine. Pure: the screen only renders `state` and `outcome`. */
export function applyLocation(state: MoveState, loc: ScanLocation, quarantine: boolean): { state: MoveState; outcome: LocationOutcome } {
  const cell = cellOf(loc);
  switch (state.step) {
    case 'SOURCE': {
      if (!SOURCE_KINDS.includes(loc.kind)) return { state, outcome: 'SOURCE_NOT_ALLOWED' };
      if (state.dest && state.dest.id === cell.id) return { state, outcome: 'SAME_CELL' };
      return { state: { ...state, source: cell, step: state.dest ? 'ITEMS' : 'DEST' }, outcome: 'SOURCE_SET' };
    }
    case 'DEST': {
      if (state.source && state.source.id === cell.id) return { state, outcome: 'SAME_CELL' };
      if (loc.kind === 'QUARANTINE') {
        if (!quarantine) return { state, outcome: 'QUARANTINE_OFF' };
      } else if (!loc.isStorage) {
        return { state, outcome: 'DEST_NOT_ALLOWED' };
      }
      return { state: { ...state, dest: cell, step: 'ITEMS' }, outcome: 'DEST_SET' };
    }
    default:
      return { state, outcome: 'RELOCATE_HINT' };
  }
}

/** «Сменить источник»: the source is scanned again; the destination stays. */
export function changeSource(state: MoveState): MoveState {
  return { ...state, source: null, step: 'SOURCE' };
}

/** «Сменить назначение»: needs a source first (the machine never skips a step). */
export function changeDest(state: MoveState): MoveState {
  return { ...state, dest: null, step: state.source ? 'DEST' : 'SOURCE' };
}

/** Turning Карантин off while a quarantine cell is the destination clears it. */
export function setQuarantine(state: MoveState, on: boolean): MoveState {
  if (!on && state.dest?.kind === 'QUARANTINE') return changeDest(state);
  return state;
}

/** Products may be scanned only once both cells are known. */
export function canScanProducts(state: MoveState): boolean {
  return state.step === 'ITEMS' && state.source !== null && state.dest !== null;
}

export const tallyKey = (skuId: string, sourceId: string, destId: string): string => `${skuId}:${sourceId}:${destId}`;

export const LOG_LIMIT = 20;
